import { CalendarDaysIcon, CheckIcon } from "@heroicons/react/24/outline";
import { RegionId } from "@sp-crm/core";
import { useQuery } from "@tanstack/react-query";
import { EventListEntryDto } from "components/calendar/calendar-types";
import { inviteeSummaryText } from "components/calendar/invitee-copy";
import { formatEventDay, formatEventTime } from "components/events/event-format";
import { trpc } from "components/rpc/trpc";
import { ActivityChip } from "components/tasks/activity-chip";
import { useActivityTypeName } from "components/tasks/activity-type-name";
import { SecondaryCompactButton } from "components/ui/secondary-button";
import React, { useState } from "react";
import { Link, useHistory } from "react-router-dom";
import { useFeature, useRegionId } from "store/selectors/hooks";

export const AgendaEventOutcomes: React.FC = () => {
    const eventsEnabled = useFeature("events");
    const regionId = useRegionId();
    const trpcClient = trpc.useTRPC();
    const [before] = useState(() => new Date());

    const query = useQuery({
        ...trpcClient.event.listAwaitingOutcome.queryOptions({
            regionId: regionId ?? ("" as RegionId),
            before,
        }),
        enabled: eventsEnabled && !!regionId,
    });

    const events = query.data ?? [];
    if (events.length === 0) {
        return null;
    }

    return (
        <div className="overflow-hidden rounded-lg bg-white ring-1 ring-gray-300">
            <div className="flex items-center gap-2 border-b border-gray-200 px-4 py-3">
                <span className="h-5 w-1 flex-shrink-0 rounded-full bg-amber-500" />
                <CalendarDaysIcon className="h-4 w-4 flex-shrink-0 text-gray-600" />
                <span className="text-sm font-semibold text-gray-800">
                    Log event outcomes
                </span>
                <span className="text-xs text-gray-500">{events.length}</span>
            </div>
            <ul className="divide-y divide-gray-100">
                {events.map(event => (
                    <EventOutcomeRow key={event.id} event={event} />
                ))}
            </ul>
        </div>
    );
};

const EventOutcomeRow: React.FC<{ event: EventListEntryDto }> = ({ event }) => {
    const history = useHistory();
    const typeName = useActivityTypeName(event.activityTypeId);
    const day = formatEventDay(event);
    return (
        <li className="flex items-start gap-3 px-4 py-2.5 hover:bg-gray-50">
            <div className="flex w-12 flex-shrink-0 flex-col items-center rounded-md border border-brand-200 bg-brand-50 px-1 py-1">
                <div className="text-[10px] font-bold uppercase leading-tight text-brand-700">
                    {day.month}
                </div>
                <div className="text-base font-bold leading-tight text-brand-900">
                    {day.day}
                </div>
            </div>
            <div className="min-w-0 flex-1">
                <Link
                    to={`/events/${event.id}`}
                    className="text-sm font-medium leading-tight text-gray-900 hover:underline">
                    {event.title}
                </Link>
                {typeName ? (
                    <div className="mt-1">
                        <ActivityChip category="event" label={typeName} />
                    </div>
                ) : null}
                <div className="mt-0.5 text-xs text-gray-500">
                    {formatEventTime(event)} · {inviteeSummaryText(event.inviteeStats)}
                </div>
            </div>
            <div className="flex flex-shrink-0 items-center self-center">
                <SecondaryCompactButton
                    className="items-center gap-1"
                    onClick={() => history.push(`/events/${event.id}?outcome=1`)}>
                    <CheckIcon className="h-3.5 w-3.5 flex-shrink-0" />
                    Log outcome
                </SecondaryCompactButton>
            </div>
        </li>
    );
};
